import type { RefObject } from 'react';
import type { App } from '@hangar/shared';
import { BitrisePanel } from '@/features/source-control/BitrisePanel';
import { useSeesProviders } from '@/lib/useAudience';
import { FormErrorAlert } from '@/components/FormErrorAlert';
import { Button } from '@/components/ui/button';
import { BuildProviderField, FrameworkField, NameField, SlugField, WorkingDirectoryField } from './ConnectAppFields';
import { useConnectAppForm } from './useConnectAppForm';

interface Props {
  projectId: string;
  onDone: (app: App) => void;
  onCancel: () => void;
  slugRef?: RefObject<HTMLInputElement | null>;
}

/** Last step for a client (SPEC decision 23): Malible prepares the native builds; nothing to pick here. */
function ClientNativeStep({ app, onDone }: { app: App; onDone: (app: App) => void }) {
  return (
    <div data-testid="app-native-step" className="fade-in flex flex-col gap-4">
      <p className="text-[13px] text-muted-foreground">
        <span className="font-medium text-foreground">{app.name}</span> is connected. Hangar prepares native builds for this app: Run build starts them once they are ready.
      </p>
      <div className="flex justify-end border-t pt-4">
        <Button data-testid="app-native-done" onClick={() => onDone(app)}>
          Done
        </Button>
      </div>
    </div>
  );
}

/** Last step of the sheet: the app is connected, its Bitrise app is created (or skipped). Operators only. */
function BitriseStep({ app, onDone }: { app: App; onDone: (app: App) => void }) {
  if (!useSeesProviders()) return <ClientNativeStep app={app} onDone={onDone} />;
  return (
    <div data-testid="app-bitrise-step" className="fade-in flex flex-col gap-4">
      <p className="text-[13px] text-muted-foreground">
        <span className="font-medium text-foreground">{app.name}</span> is connected. Last step: its Bitrise app.
      </p>
      <BitrisePanel app={app} onDone={() => onDone(app)} />
      <div className="flex justify-end border-t pt-4">
        <Button variant="ghost" data-testid="app-bitrise-skip" onClick={() => onDone(app)}>
          Skip for now
        </Button>
      </div>
    </div>
  );
}

function ConnectFooter({ onCancel, pending }: { onCancel: () => void; pending: boolean }) {
  return (
    <div className="flex items-center justify-end gap-2 border-t pt-4">
      <Button variant="ghost" onClick={onCancel}>
        Cancel
      </Button>
      <Button type="submit" data-testid="app-submit" loading={pending}>
        Connect app
      </Button>
    </div>
  );
}

/**
 * Screen 15, form only (the wizard's checklist steps are later phases). Expo apps build today; native frameworks are
 * listed but not selectable until the API reports native builds (`bitrise`).
 */
export function ConnectAppForm({ projectId, onDone, onCancel, slugRef }: Props) {
  const f = useConnectAppForm({ projectId, onDone });
  if (f.created) return <BitriseStep app={f.created} onDone={onDone} />;
  return (
    <form data-testid="app-form" id="app-form" onSubmit={(e) => void f.onSubmit(e)} noValidate className="fade-in flex flex-col gap-4" aria-label="Connect app">
      <div className="grid gap-4 sm:grid-cols-2">
        <NameField f={f} />
        <SlugField f={f} slugRef={slugRef} />
        <FrameworkField f={f} />
        <WorkingDirectoryField f={f} />
        {/* No "Config path" field: the hangar.yml sync is deferred (docs/mvp-plan.md P9); the default path is sent. */}
        <BuildProviderField f={f} />
      </div>
      <FormErrorAlert message={f.formError} />
      <ConnectFooter onCancel={onCancel} pending={f.pending} />
    </form>
  );
}
